import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import type { ComponentProps, ReactNode } from 'react';
import { MinusIcon, PlusIcon } from '../lib/icons.tsx';
import { cx } from '../lib/styled.tsx';
import { fieldHint, fieldLabel } from '../lib/surfaces.ts';

const stepper =
  'flex h-11 w-10 shrink-0 items-center justify-center text-ink-muted select-none ' +
  'transition-colors duration-150 ease-out-quint ' +
  'hover:not-data-disabled:bg-surface-muted hover:not-data-disabled:text-ink ' +
  'data-disabled:cursor-not-allowed data-disabled:opacity-50';

export interface NumberFieldProps extends ComponentProps<typeof BaseNumberField.Root> {
  label?: ReactNode;
  description?: ReactNode;
  className?: string;
}

/**
 * A number input with steppers, locale-aware formatting and keyboard stepping.
 *
 * The label doubles as a scrub area: dragging sideways over it changes the value,
 * which is the fastest way to set a number that has no meaningful keyboard step.
 */
export function NumberField({ label, description, className, ...rootProps }: NumberFieldProps) {
  return (
    <BaseNumberField.Root className={cx('flex flex-col gap-1.5', className)} {...rootProps}>
      {label ? (
        <BaseNumberField.ScrubArea className={cx('w-fit cursor-ew-resize select-none', fieldLabel)}>
          {label}
          <BaseNumberField.ScrubAreaCursor />
        </BaseNumberField.ScrubArea>
      ) : null}
      <BaseNumberField.Group
        className={cx(
          'flex w-fit items-stretch overflow-hidden rounded-control border border-border-subtle bg-surface',
          'focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-400/35',
          'data-invalid:border-danger data-invalid:ring-danger/20',
          'data-disabled:bg-surface-muted data-disabled:text-ink-faint',
        )}
      >
        <BaseNumberField.Decrement className={cx(stepper, 'border-r border-border-subtle')}>
          <MinusIcon className="size-4" />
        </BaseNumberField.Decrement>
        <BaseNumberField.Input className="h-11 w-20 bg-transparent px-3 text-center text-md tabular-nums text-ink outline-none" />
        <BaseNumberField.Increment className={cx(stepper, 'border-l border-border-subtle')}>
          <PlusIcon className="size-4" />
        </BaseNumberField.Increment>
      </BaseNumberField.Group>
      {description ? <p className={fieldHint}>{description}</p> : null}
    </BaseNumberField.Root>
  );
}
